/**
 * Tasks Overview Card
 *
 * Donut chart showing tasks by status.
 */

import { useMemo } from 'react';
import ChartContainer from '@/components/charts/ChartContainer';
import PieChart from '@/components/charts/PieChart';
import type { TasksSummary } from '../types';

interface TasksOverviewCardProps {
  data?: TasksSummary;
  loading?: boolean;
}

const STATUS_COLORS: Record<string, string> = {
  todo: '#6b7280', // gray
  in_progress: '#3b82f6', // blue
  blocked: '#ef4444', // red
  done: '#22c55e', // green
  cancelled: '#a855f7', // purple
};

const STATUS_LABELS: Record<string, string> = {
  todo: 'To Do',
  in_progress: 'In Progress',
  blocked: 'Blocked',
  done: 'Done',
  cancelled: 'Cancelled',
};

export function TasksOverviewCard({ data, loading }: TasksOverviewCardProps) {
  const { chartData, colors } = useMemo(() => {
    if (!data?.by_status) return { chartData: [], colors: [] };
    const entries = Object.entries(data.by_status).filter(([_, count]) => count > 0);
    return {
      chartData: entries.map(([status, count]) => ({ name: STATUS_LABELS[status] || status, value: count })),
      colors: entries.map(([status]) => STATUS_COLORS[status] || '#6b7280'),
    };
  }, [data]);

  const hasData = chartData.length > 0;

  return (
    <ChartContainer
      title="Tasks by Status"
      loading={loading}
      height={220}
    >
      {hasData ? (
        <PieChart
          data={chartData}
          height={220}
          donut
          showLegend
          colors={colors}
        />
      ) : (
        <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
          No task data available
        </div>
      )}
    </ChartContainer>
  );
}
